import {
  type FishDefinition,
  type WaterKind,
  pickFish,
  rollSize,
  windowFor,
} from '@/data/fish';
import { describeCatch } from '@/data/fish';
import type { Season } from '@/time/clock';

/**
 * The fishing minigame.
 *
 * Two beats: wait for a bite, then land it. The wait is dead time on purpose —
 * it is the quiet the whole activity is for — but it is bounded so it never
 * becomes tedious, and striking early costs the cast so the waiting means
 * something.
 *
 * A pure state machine with no rendering or input handling, so the timings are
 * testable without a browser.
 */

export type FishingPhase = 'casting' | 'bite' | 'landing' | 'caught' | 'missed';

/** Seconds between casting and a bite. */
const MIN_WAIT = 1.2;
const MAX_WAIT = 3.6;
/** How long the player has to react to a bite. */
const BITE_WINDOW = 0.9;
/** Seconds for the strike marker to cross the bar and come back. */
const LANDING_PERIOD = 1.4;
/** A landing attempt is abandoned after this long. */
const LANDING_TIMEOUT = 6;

export interface FishingResult {
  readonly fish: FishDefinition | null;
  /** Length in inches, when something was actually on the line. */
  readonly inches: number;
  readonly reason:
    | 'caught'
    | 'too-early'
    | 'too-slow'
    | 'missed-strike'
    | 'nothing-biting'
    | 'no-bait';
}

export interface FishingOptions {
  readonly water: WaterKind;
  readonly season: Season;
  readonly hour: number;
  /** Strength of the rod in hand. */
  readonly rodTier: number;
  /** What is on the hook. Without bait nothing is interested. */
  readonly bait: string | null;
  /** Injectable randomness, so casts are reproducible under test. */
  readonly random?: () => number;
}

export class FishingSession {
  phase: FishingPhase = 'casting';
  /** Marker position along the strike bar, 0..1. */
  marker = 0;
  result: FishingResult | null = null;

  private readonly random: () => number;
  private readonly waitFor: number;
  private elapsed = 0;
  private readonly hooked: FishDefinition | null;
  /** Length of the fish on the line. Rolled at cast, not at landing, so the
   *  difficulty the player faces matches the fish they are told they caught. */
  readonly hookedIn: number;

  constructor(options: FishingOptions) {
    this.random = options.random ?? Math.random;
    this.waitFor = MIN_WAIT + this.random() * (MAX_WAIT - MIN_WAIT);
    this.hooked = pickFish(
      {
        water: options.water,
        season: options.season,
        hour: options.hour,
        rodTier: options.rodTier,
        bait: options.bait,
      },
      this.random(),
    );
    this.hookedIn = this.hooked ? rollSize(this.hooked, this.random(), this.random()) : 0;

    if (!this.hooked) {
      this.phase = 'missed';
      this.result = {
        fish: null,
        inches: 0,
        reason: options.bait === null ? 'no-bait' : 'nothing-biting',
      };
    }
  }

  get isOver(): boolean {
    return this.phase === 'caught' || this.phase === 'missed';
  }

  /** The fish on the line, once there is one to show. */
  get fish(): FishDefinition | null {
    return this.hooked;
  }

  /** Centre of the target zone, 0..1. Only meaningful while landing. */
  get targetCentre(): number {
    return 0.5;
  }

  /** Half-width of the target zone, as a fraction of the bar. */
  get targetHalfWidth(): number {
    return (this.hooked ? windowFor(this.hooked, this.hookedIn) : 0.3) / 2;
  }

  update(dt: number): void {
    if (this.isOver) return;
    this.elapsed += dt;

    switch (this.phase) {
      case 'casting':
        if (this.elapsed >= this.waitFor) {
          this.phase = 'bite';
          this.elapsed = 0;
        }
        break;

      case 'bite':
        if (this.elapsed >= BITE_WINDOW) {
          this.fail('too-slow');
        }
        break;

      case 'landing': {
        if (this.elapsed >= LANDING_TIMEOUT) {
          this.fail('missed-strike');
          break;
        }
        // Ping-pong across the bar rather than wrapping, so the marker is never
        // discontinuous and the player can time the turn.
        const cycle = (this.elapsed % LANDING_PERIOD) / LANDING_PERIOD;
        this.marker = cycle < 0.5 ? cycle * 2 : 2 - cycle * 2;
        break;
      }

      default:
        break;
    }
  }

  /** The Use button, whatever it means at this moment. */
  strike(): void {
    if (this.isOver) return;

    switch (this.phase) {
      case 'casting':
        // Reeling in before anything bites loses the cast. Without a cost,
        // mashing would be strictly better than waiting.
        this.fail('too-early');
        break;

      case 'bite':
        this.phase = 'landing';
        this.elapsed = 0;
        this.marker = 0;
        break;

      case 'landing':
        if (Math.abs(this.marker - this.targetCentre) <= this.targetHalfWidth) {
          this.phase = 'caught';
          this.result = { fish: this.hooked, inches: this.hookedIn, reason: 'caught' };
        } else {
          this.fail('missed-strike');
        }
        break;

      default:
        break;
    }
  }

  private fail(reason: FishingResult['reason']): void {
    this.phase = 'missed';
    this.result = { fish: null, inches: 0, reason };
  }
}

/** Player-facing wording for how a cast ended. */
export function describeResult(result: FishingResult): string {
  switch (result.reason) {
    case 'caught':
      return result.fish
        ? `Caught ${describeCatch(result.fish, result.inches)}!`
        : 'Caught a fish!';
    case 'too-early':
      return 'Reeled in too soon';
    case 'too-slow':
      return 'It got away';
    case 'missed-strike':
      return 'The line went slack';
    case 'nothing-biting':
      return 'Nothing here wants what you are offering';
    case 'no-bait':
      return 'You need bait on the hook';
  }
}
